import * as React from "react";
import { Dropdown, IDropdownOption, ResponsiveMode, IStackTokens, Stack, ActionButton, StackItem, TextField, IStackStyles, Label } from "@fluentui/react";
import { getLibraryDefaultValues, getListSettings, getSharePointContentTypeFields, getSharePointContentTypes, getUserEffectivePermissions } from "../../../services/SharePointRestServices";
import { ComponentTypes } from "../../../config/config";
import InputMetaDataFactory from "../InputMetadataFactory";
import { SharepointSpServices } from "../../../services/SharepointSpServices";
import { SharePointInputData } from "../../../interfaces/props/SharePointInputData";
import { SharePointMetadataBooleans } from "../../../config/sharepointConstants";
import Loader from "../../Loader";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../../redux";
import { setSavingLocation } from "../../../redux/modules/savingLocation";
import { setIsBusyLoadingMetadata, setIsCheckingForDuplicates } from "../../../redux/modules/savingInBackground";
import { setFile, setFileExpander, setFilesCanOverwrite } from "../../../redux/modules/file";
import { addRegionalSettings } from "../../../redux/modules/regional";
import authModule from "../../../../../utilities/AuthModule";
import { LanguageContext } from "../../../../../utilities/LocalizationModule";
import { DefaultValueService } from "../../../services/DefaultValueService";
import { BaseSharepointHelper, SharepointContentTypeHelper } from "../../helpers/sharepointContentTypeHelper";
import { DocubirdCache, internalSharepointName, piiConfig, SharepointFilters, SharePointFolderContentTypeIds, sharePointInternalFileName, sharePointInternalTitleName } from "../../../config/constants";
import { FolderView } from "../../helpers/folderView";
import { getDriveItemSharepointUniqueId, getDriveRootWebUrl, getProtectionLabels } from "../../../services/GraphService"
import { InfoProtectionFileTypeEnum } from "../../enums/InformationProtectionFileTypesEnum"
import savingLocationStructureModule from "../../../../../utilities/SavingLocationStructureModule";
import { logger } from "../../../../../utilities/logger";
import { IOfficeService } from "../../../interfaces/IOfficeService";
import emailMapper, { IMappedValue } from "../../../services/EmailMapper";
import { OutlookService } from "../../../services/OutlookService";
import { OutlookUtils } from "../../../../../utilities/OutlookUtils";
import { axiosRetryGet } from "../../../../../utilities/AxiosRetryUtil";
import { IDocumentSetSettings } from "../../../interfaces/IDocumentSetSettings";
import { IContentType } from "../../../interfaces/IContentType";
import { useFiles } from "../../_v2/redux/hooks/useFiles";
import generalModule from "../../../../../utilities/GeneralModule";
import { setCanViewFolder } from "../../../redux/modules/currentSharepointFolder";
import { FileOption } from "../../../interfaces/IFileOption";
import { editFilePropertiesButtonStyles } from "../../../config/controlStyles";
import { boundField, currentSharepointFolderUserPermissions } from "../../../redux/models/reduxModels";
import { DatasourceFieldHelper } from "../../helpers/dataSourceFieldHelper";
import { ModeEnum } from "../../enums/modeEnum";
import { IFileToUpload } from "../../../interfaces/IFileToUpload";
import { IFilePreCommitResult } from "../../../interfaces/IFilePreCommitResult";
import { IPageSettings } from "../../../interfaces/IPageSettings";
import { OneDriveUrlHelper } from "../../helpers/oneDriveUrlHelper";
import { DragAndDropHelper } from "../../../../../utilities/DragAndDropHelper";
import { ContentManagerCopyMoveModeEnum } from "../../enums/ContentManagerCopyMoveModeEnum";
import { CacheHelper } from "../../../../../utilities/CacheHelper";
import { ISharePointToEmailMapping } from "../../_v2/redux/api/local/api-local-interfaces";
import { apiFilePreCommitCheck } from "../../../services/ApiService";
import { ILibraryDefault, ILibraryDefaultValue } from "../../../interfaces/ILibraryDefaults";
import { tryDecodeUriComponent } from "../../../../../utilities/EncodeDecode";
import { DatasourceHelper } from "../../helpers/dataSourceHelper";
import { DatasourceService } from "../../../services/DatasourceService";
import { formatNameFieldValue } from "../../../services/FileFolderNameExtensions";
import { IMailSendConfig } from "../../../interfaces/IMailSendPayload";
import useDidMountEffect from "../../../../../utilities/Hooks/useDidMountEffect";

const multipleMetaUpdateIcon = { iconName: "Edit" };

const inputFieldsStackStyles: React.CSSProperties = {
  paddingBottom: 15
}

const loaderStackStyles: Partial<IStackStyles> = {
  root: { width: "100%", height: "100%", margin: 0, paddingTop: 20, overflow: "auto" }
};

const stackTokens: IStackTokens = {
  childrenGap: 15
};

const undefinedString: string = "_undefined_";

const mapStateToProps = (state: RootState) => {
  return {
    workingDirectories: state.workingDirectories?.workingDirectories,
    isSavingInBackground: state.savingInBackground?.isSavingInBackground,
    currentDirectory:
      state.workingDirectories?.workingDirectories[state.workingDirectories?.workingDirectories?.length - 1],
    files: state.files?.files,
    regionalSettings: state.regionalSettings,
    siteInfo: state.tenantInfo?.sites,
    hiddenMetadataFields: state.tenantInfo?.hiddenMetadataFields,
    reload: state.reload?.toggle,
    currentSharepointFolder: state.currentSharepointFolder,
    tenantReplaceFileNameWithGUID: state.tenantInfo?.replaceFileNameWithGUID,
    tenantDisableFileRename: state.tenantInfo?.disableFileRename,
    datasources: state.tenantInfo?.datasources,
    mode: state.mode?.mode,
    moveTo: state.contentManagerDirectory?.moveTo,
    convertEmailToPDF: state.user?.settings.convertEmailToPDF,
    oneDriveWebUrl: state.user?.oneDriveWebUrl,
    oneDriveRootDetails: state.workingDirectories?.oneDriveRootDetails
  };
};

const mapDispatchToProps = {
  setIsCheckingForDuplicates,
  setSavingLocation,
  setFilesCanOverwrite,
  addRegionalSettings,
  setCanViewFolder,
  setFile,
  setFileExpander,
  setIsBusyLoadingMetadata
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface SaveWithMetaDataProps extends PropsFromRedux {
  updateSharepointMetadataToSave: (value: SharePointInputData[], informationProtectionId: string, changedIndex?: number) => void;
  inputValuesToSave: SharePointInputData[];
  OfficeService?: IOfficeService;
  isInfoMode?: boolean;
  addNameField?: boolean;
  disableNameField?: boolean;
  isCreateFromTemplate?: boolean;
  isInOneDrive?: boolean;
  itemContentTypeId?: string;
  itemWebUrl?: string;
  itemSharepointListId?: string;
  fieldsChanged?: (fields: any[], contentTypeId: string, inputValues?: SharePointInputData[]) => void;
  fieldsLoaded?: () => void;
  selectedIndividualFile?: FileOption;
  setSelectedIndividualFile?: (selectedIndividualFile: FileOption) => void;
  showUpdateIndividualFilesButton?: boolean;
  setIsCMMultipleFilesMetaDataPanelOpen?: (isCMMultipleFilesMetaDataPanelOpen: boolean) => void;
  userItemPermissions?: currentSharepointFolderUserPermissions;
  showSelectedFileCount?: boolean;
  isCurrentDirectorySPList?: boolean;
  isBulkEdit?: boolean;
  mailSendConfig?: IMailSendConfig;
}

const SaveWithMetaData: React.FunctionComponent<SaveWithMetaDataProps> = ({
  updateSharepointMetadataToSave,
  inputValuesToSave,
  setSavingLocation,
  setIsCheckingForDuplicates,
  setCanViewFolder,
  workingDirectories,
  currentDirectory,
  files,
  setFilesCanOverwrite,
  regionalSettings,
  addRegionalSettings,
  siteInfo,
  hiddenMetadataFields,
  OfficeService,
  reload,
  currentSharepointFolder,
  isInfoMode,
  addNameField,
  disableNameField,
  isInOneDrive,
  itemContentTypeId,
  itemWebUrl,
  itemSharepointListId,
  fieldsLoaded,
  fieldsChanged,
  tenantReplaceFileNameWithGUID,
  tenantDisableFileRename,
  isCreateFromTemplate,
  selectedIndividualFile,
  setSelectedIndividualFile,
  showUpdateIndividualFilesButton,
  setIsCMMultipleFilesMetaDataPanelOpen,
  datasources,
  mode,
  setFile,
  moveTo,
  userItemPermissions,
  showSelectedFileCount,
  setFileExpander,
  convertEmailToPDF,
  isCurrentDirectorySPList,
  isBulkEdit,
  mailSendConfig,
  oneDriveWebUrl,
  oneDriveRootDetails,
  setIsBusyLoadingMetadata
}: SaveWithMetaDataProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const [fileList, setFileList] = React.useState<IFileToUpload[]>(!DragAndDropHelper.isDroppedModeActive ? files : files.filter(f => f.isFileSystemEntry));
  const [selectedFileList, setSelectedFileList] = React.useState<IFileToUpload[]>(fileList?.filter(x => x.isSelected) ?? []);
  const [isLoading, setLoading] = React.useState<boolean>(true);
  const [contentTypes, setContentTypes] = React.useState<IDropdownOption[]>([]);
  const [documentSetSettings, setDocumentSetSettings] = React.useState<IDocumentSetSettings>();
  const [fields, setFields] = React.useState([]);
  const [selectedContentType, setSelectedContentType] = React.useState<IDropdownOption>();

  const [protectionLabels, setProtectionLabels] = React.useState<IDropdownOption[]>([]);
  const [selectedProtectionLabel, setSelectedProtectionLabel] = React.useState<IDropdownOption>();
  const [showProtectionDropdown, setShowProtectionDropdown] = React.useState<boolean>(false);

  const [sharepointService, setSharepointService] = React.useState<SharepointSpServices>(null);
  const [datasourceFields] = React.useState<boundField[]>(DatasourceFieldHelper.getDatasourceFields(datasources, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""]));

  // const [officeFileName, setOfficeFileName] = React.useState<string>(mode === ModeEnum.office && fileList?.length === 1 ? fileList[0].name : "");
  const [selectedFileName, setSelectedFileName] = React.useState<string>(selectedFileList?.length === 1 ? selectedFileList[0].name : "");

  const emailMappings = React.useRef<ISharePointToEmailMapping[]>([]);
  const locationDefaultValues = React.useRef<ILibraryDefaultValue[]>(null);

  const { getFilePreCommitCheckAsync } = useFiles();

  const isOneDriveBusiness = React.useRef<boolean>(isInOneDrive
    || (itemWebUrl ?? currentDirectory?.webUrl)?.startsWith(oneDriveRootDetails?.sharePointSiteUrl ?? undefinedString)
    || (itemWebUrl ?? currentDirectory?.webUrl)?.startsWith(oneDriveWebUrl ?? undefinedString)
    || oneDriveWebUrl?.startsWith((itemWebUrl ?? currentDirectory?.webUrl) ?? undefinedString));

  const showStaticNameField: boolean = React.useMemo(() => {
    return !isInfoMode && !isCurrentDirectorySPList && !tenantReplaceFileNameWithGUID && !isBulkEdit;
  }, [isInfoMode, isCurrentDirectorySPList, tenantReplaceFileNameWithGUID, isBulkEdit]);

  React.useEffect(() => {
    setIsLoading(true);
    setIsCheckingForDuplicates(true);
    const setupAsyncWrapper = async () => {
      try {
        const accessToken = !isOneDriveBusiness.current
          ? await authModule.getSharePointAccessToken()
          : await authModule.getOneDriveAccessToken();

        const webUrl: string = !isOneDriveBusiness.current
          ? (itemWebUrl ?? currentDirectory.webUrl)
          : OneDriveUrlHelper.getSiteUrl(itemWebUrl ?? currentDirectory.webUrl);

        const isOneDrive: boolean = isOneDriveBusiness.current
          || (itemWebUrl ?? currentDirectory?.webUrl)?.startsWith(oneDriveRootDetails?.sharePointSiteUrl ?? undefinedString)
          || webUrl?.startsWith(oneDriveWebUrl ?? undefinedString)
          || oneDriveWebUrl?.startsWith(webUrl ?? undefinedString);

        if (isOneDrive && !isOneDriveBusiness.current) {
          isOneDriveBusiness.current = true;
        }

        const sharepointSpService = new SharepointSpServices(webUrl, accessToken);
        setSharepointService(sharepointSpService);
        const regionalSettingsAvailable = regionalSettings[webUrl];
        if (!regionalSettingsAvailable) {
          const regionalSettings = !isOneDrive ? await sharepointSpService.getSiteRegionalSettings() : null;
          if (regionalSettings) {
            addRegionalSettings({ ...regionalSettings, webUrl: webUrl });
          }
        }

        // await Promise.all([
        //   getProtectionLabelsGraph(),
        //   getContentTypes(accessToken),
        // ]);
        await getContentTypes(accessToken);

        if (!isInfoMode) {
          if (!currentDirectory.isSaveLocationStructureSet) {
            savingLocationStructureModule.setSavingLocationStructure(workingDirectories);
          }

          setSavingLocation({
            display: currentDirectory.fileTitle ?? currentDirectory.displayName,
            webUrl: webUrl,
            directory: currentDirectory.directory,
            documentLibrary: currentDirectory.DocumentLibrary,
            sharePointListId: currentDirectory.sharePointListId ?? itemSharepointListId,
            teamId: workingDirectories?.find(dir => dir.teamGroupId)?.teamGroupId,
            channelId: workingDirectories?.find(dir => dir.teamChannelId)?.teamChannelId
          }, isOneDrive);
        }

        setIsLoading(false);

        if (fieldsLoaded) {
          fieldsLoaded();
        }

        if (!isInfoMode && !tenantReplaceFileNameWithGUID) {
          if (fileList?.length > 1) {
            let destUrl: string;
            if (!isOneDrive && currentDirectory?.isTeamsRootFolder && !currentDirectory.parentId) {
              destUrl = await getDriveRootWebUrl(currentDirectory.id);
            }
            else if (isOneDrive && oneDriveWebUrl) {
              destUrl = !currentDirectory.directory ? oneDriveWebUrl : `${oneDriveWebUrl}/${currentDirectory.directory}`;
            }
            else {
              destUrl = !currentDirectory.directory ? webUrl : `${webUrl}/${currentDirectory.directory}`;
            }

            const preComitResult = await getFilePreCommitCheckAsync(
              files,
              {
                autoFolderCreation: !(new FolderView(siteInfo, currentDirectory).canViewFolder()),
                destinationUrl: destUrl,
                siteUrl: isOneDrive ? OneDriveUrlHelper.getSiteUrl(webUrl) : webUrl
              },
              isOneDrive
            );

            if (preComitResult?.some(file => file?.isDuplicate === true)) {
              setFileExpander(true);
            }
          }
          else if (selectedFileList?.length === 1) {
            setFile(await checkFile(selectedFileList[0].name));
          }
        }
      } catch (e) {
        logger(e, "SaveToSharepoint.tsx (useEffect 1)");
        setIsLoading(false);
        if (fieldsLoaded) {
          fieldsLoaded();
        }
      }
      setIsCheckingForDuplicates(false);
    };

    setupAsyncWrapper();
  }, [reload]);

  React.useEffect(() => {
    const updatedFileList = !DragAndDropHelper.isDroppedModeActive ? files : files.filter(f => f.isFileSystemEntry);
    const updatedSelectedFileList = updatedFileList?.filter(x => x.isSelected) ?? [];
    setFileList(updatedFileList);
    setSelectedFileList(updatedFileList?.filter(x => x.isSelected) ?? []);
    setSelectedFileName(updatedSelectedFileList?.length === 1 ? updatedSelectedFileList[0].name : "");
  }, [files]);

  useDidMountEffect(() => {
    let isCurrentCall = true;
    let debounceTimer;

    debounceTimer = setTimeout(async () => {
      if (!isInfoMode && selectedFileName) {
        const checkedFile: IFileToUpload = await checkFile(selectedFileName);
        if (isCurrentCall) {
          setFile(checkedFile);
        }
      }
    }, 900);

    return () => {
      isCurrentCall = false;
      clearTimeout(debounceTimer);
    };
  }, [isInfoMode, selectedFileName]);

  // React.useEffect(() => {
  //   //check if there are any files in the list of FileToUpload that are compatible with protection labels
  //   try {
  //     if (protectionLabels?.length > 0) {
  //       const someCompatible = fileList
  //         .filter(f => f.isSelected)
  //         .some(f => isCompatibleType(f.extension));
  //       setShowProtectionDropdown(someCompatible);
  //       if (!someCompatible) {
  //         setSelectedProtectionLabel({
  //           key: null,
  //           text: "",
  //           title: ""
  //         });
  //       }
  //     } else {
  //       setShowProtectionDropdown(false);
  //       setSelectedProtectionLabel({
  //         key: null,
  //         text: "",
  //         title: ""
  //       });
  //     }
  //   } catch (e) {
  //     logger(e, "SaveToSharepoint.tsx (useEffect 2)");
  //   }

  // }, [fileList, protectionLabels, selectedIndividualFile]);

  useDidMountEffect(() => {
    const asyncWrapper = async () => {
      await getMailExchangeInformation();

      let inputValuesToSaveFields: SharePointInputData[] = await Promise.all(fields.map(async (item, index) => {
        return await setupInputValues(item, index);
      }));

      //Typecasting here, because ".key" can be int or string
      const sensitivityLabelId: string = selectedProtectionLabel ? selectedProtectionLabel.key as string : null;
      updateSharepointMetadataToSave(inputValuesToSaveFields, sensitivityLabelId);
    }

    asyncWrapper();
  }, [OfficeService]);

  const setIsLoading = (isLoading: boolean) => {
    setLoading(isLoading);
    setIsBusyLoadingMetadata(isLoading);
  }

  const checkFile = async (selectedFileName: string) => {
    let file: IFileToUpload = { ...(selectedFileList[0]), name: selectedFileName };
    let preCommitResult: IFilePreCommitResult;
    if (selectedFileName) {
      setIsCheckingForDuplicates(true);
      try {
        const saveLocation: IPageSettings = savingLocationStructureModule.getSavingDestination() ?? currentDirectory;

        const isOneDrive: boolean = isOneDriveBusiness.current
          || (itemWebUrl ?? currentDirectory?.webUrl)?.startsWith(oneDriveRootDetails?.sharePointSiteUrl ?? undefinedString)
          || saveLocation.webUrl?.startsWith(oneDriveWebUrl ?? undefinedString)
          || oneDriveWebUrl?.startsWith(saveLocation.webUrl ?? undefinedString);

        if (isOneDrive && !isOneDriveBusiness.current) {
          isOneDriveBusiness.current = true;
        }

        if (saveLocation) {
          let destUrl: string;
          if (!isOneDrive && saveLocation.isTeamsRootFolder && !saveLocation.parentId) {
            destUrl = await getDriveRootWebUrl(currentDirectory.id);
          }
          else if (isOneDrive && oneDriveWebUrl) {
            destUrl = !saveLocation.directory ? oneDriveWebUrl : `${oneDriveWebUrl}/${saveLocation.directory}`;
          }
          else {
            destUrl = !saveLocation.directory ? saveLocation.webUrl : `${saveLocation.webUrl}/${saveLocation.directory}`;
          }

          preCommitResult = await apiFilePreCommitCheck({
            accessToken: !isOneDrive ? await authModule.getSharePointAccessToken() : await authModule.getOneDriveAccessToken(),
            autoFolderCreation: isOneDrive ? false : !(new FolderView(siteInfo, saveLocation).canViewFolder()),
            fileName: (file.isMainMailItem && convertEmailToPDF) ? `${file.name}.pdf` : `${file.name}.${file.extension}`,
            destinationUrl: destUrl,
            siteUrl: isOneDrive ? OneDriveUrlHelper.getSiteUrl(saveLocation.webUrl) : saveLocation.webUrl
          });
        }
      } catch (error) {
        logger(error, "SaveToSharepoint.tsx (checkFile)");
      }
      setIsCheckingForDuplicates(false);
    }

    if (!preCommitResult) {
      preCommitResult = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null };
    }

    const fileToSet: IFileToUpload = {
      ...file,
      name: selectedFileName,
      isDuplicate: preCommitResult.exists,
      filePreCommitResult: preCommitResult
    }

    return fileToSet;
  }

  const getMailExchangeInformation = async (mailSendConfig?: IMailSendConfig) => {
    let mailItemRestId: string;
    let mailItemRestUrl: string;
    let token: string;

    if (!mailSendConfig) {
      const itemId = !generalModule.isInConentMangerMode() ? Office?.context?.mailbox?.item?.itemId : null;
      if (itemId) {
        mailItemRestId = OutlookService.getItemRestId(itemId);
        mailItemRestUrl = await OutlookUtils.getRestURL();
        token = await OutlookUtils.getOutlookToken();
      }
    }
    else {
      mailItemRestId = mailSendConfig.mailItemRestId;
      mailItemRestUrl = mailSendConfig.restURL;
      token = mailSendConfig.outlookToken;
    }

    if (mailItemRestId && mailItemRestUrl && token) {
      const updateMessageUrl = mailItemRestUrl + "/messages/" + mailItemRestId;
      const headers = {
        "Authorization": "Bearer " + token
      }

      try {
        const response = (await axiosRetryGet(updateMessageUrl, {
          headers: headers
        }))?.data;
        emailMapper.updateValuesFromExchange(response);
      } catch (e) {
        logger(e, "SaveToSharepoint.tsx (getMailExchangeInformation)");
      }
    }
  }

  // const isCompatibleType = (extension: string): boolean => {
  //   const objVals = Object.keys(InfoProtectionFileTypeEnum);
  //   let any = objVals.filter((value) => {
  //     return value.toLowerCase() === extension?.trim().toLowerCase()
  //   })
  //   return any.length > 0;
  // }

  const getContentTypes = async (accessToken: string) => {
    if (!isOneDriveBusiness.current) {
      const documentSetContentTypeId: string =
        currentDirectory?.parentDocumentSetContentTypeId
        ??
        workingDirectories?.find(dir => dir?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet))?.contentTypeId;

      let [contentTypeResult, documentSetSettings, libraryDefaults] = await Promise.all(
        [
          getSharePointContentTypes(accessToken, itemWebUrl ?? currentDirectory.webUrl, itemSharepointListId ?? currentDirectory.sharePointListId, documentSetContentTypeId ? true : false),
          getDocumentSetSettings(accessToken, documentSetContentTypeId),
          getLibraryDefaultValues(accessToken, itemWebUrl ?? currentDirectory?.webUrl, currentDirectory?.directory),
          getMailExchangeInformation(mailSendConfig),
          getLibrarySettings(),
        ]
      );

      setLocationDefaultValues(libraryDefaults);

      if (contentTypeResult?.length > 0) {
        const isFolderOrDocumentSetContentType: boolean = itemContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder);
        let contentTypes: IContentType[];
        if (!isFolderOrDocumentSetContentType) {
          contentTypes = SharepointContentTypeHelper.mapDocumentContentTypes(contentTypeResult);
        }
        else {
          contentTypes = SharepointContentTypeHelper.mapFolderOrDocumentSetContentTypes(contentTypeResult, itemContentTypeId);
        }

        if (!isFolderOrDocumentSetContentType && documentSetSettings?.allowedContentTypeIds?.length > 0) {
          let allowedContentTypes = contentTypes.filter(contentType => documentSetSettings.allowedContentTypeIds.some(allowedContentType => allowedContentType === contentType.parent?.id));
          if (allowedContentTypes.length > 0) { // Ensure to always show content types should filter not return any results
            contentTypes = allowedContentTypes;
          }
        }

        let availableContentTypeDropdownOptions: IDropdownOption[];
        if (isBulkEdit && !itemContentTypeId) {
          availableContentTypeDropdownOptions = [{ key: "", text: "" }].concat(contentTypes.map(contentType => ({ key: contentType.id, text: contentType.name })));
        }
        else {
          availableContentTypeDropdownOptions = contentTypes.map(contentType => ({ key: contentType.id, text: contentType.name }));
        }
        setContentTypes(availableContentTypeDropdownOptions);

        if (availableContentTypeDropdownOptions.length > 0) {
          let defaultSelectedContentType: IDropdownOption;

          if (!isBulkEdit || itemContentTypeId) {
            if (!itemContentTypeId) {
              const mappedContentTypes: string[] = emailMapper.findMappedColumnNames("contentType");
              if (mappedContentTypes) {
                defaultSelectedContentType = availableContentTypeDropdownOptions.find(contentType => mappedContentTypes.some(mappedContentType => contentType.text.toLowerCase() === mappedContentType.toLowerCase()));
              }
            }
            else {
              defaultSelectedContentType = availableContentTypeDropdownOptions.find(contentType => contentType.key == itemContentTypeId);
            }

            if (!defaultSelectedContentType) {
              defaultSelectedContentType = availableContentTypeDropdownOptions.find(contentType => (contentType.key as string)?.startsWith(SharePointFolderContentTypeIds.document)) ?? availableContentTypeDropdownOptions[0];
            }

            setSelectedContentType(defaultSelectedContentType);
          }
          else {
            defaultSelectedContentType = availableContentTypeDropdownOptions[0];
          }

          const { fields, inputValuesToSaveFields } = await getFields(accessToken, documentSetSettings?.sharedFieldIds, defaultSelectedContentType.key as string, defaultSelectedContentType.text);
          if (fieldsChanged) fieldsChanged(fields, defaultSelectedContentType.key as string, inputValuesToSaveFields);
        }
      }
    } else {
      const { fields } = await getFields(accessToken, documentSetSettings?.sharedFieldIds);
      if (fieldsChanged) fieldsChanged(fields, '');
    }
  };

  const getLibrarySettings = async () => {
    if (!generalModule.isInConentMangerMode()) {
      const accessToken = !isOneDriveBusiness.current
        ? await authModule.getSharePointAccessToken()
        : await authModule.getOneDriveAccessToken();

      const webUrl: string = !isOneDriveBusiness.current
        ? (itemWebUrl ?? currentDirectory.webUrl)
        : OneDriveUrlHelper.getSiteUrl(itemWebUrl ?? currentDirectory.webUrl);

      const listId = currentDirectory?.sharePointListId || itemSharepointListId;
      let uniqueId = currentDirectory?.sharePointUniqueId;
      if (currentDirectory?.parentId && !uniqueId) {
        uniqueId = await getDriveItemSharepointUniqueId(currentDirectory.parentId, currentDirectory.id);
      }

      const refreshSettings = currentSharepointFolder?.sharepointListId !== currentDirectory?.sharePointListId;
      const refreshPermissions = (!currentSharepointFolder?.userPermissions && !userItemPermissions)
        || currentSharepointFolder?.sharepointListId !== listId
        || (currentSharepointFolder?.sharePointUniqueId !== uniqueId);

      const [settings, permissions] = await Promise.all([
        refreshSettings && getListSettings(accessToken, webUrl, listId),
        refreshPermissions && getUserEffectivePermissions(accessToken, webUrl, listId, uniqueId)
      ]);

      if (refreshSettings || refreshPermissions) {
        setCanViewFolder({
          ...currentSharepointFolder,
          sharepointListId: listId,
          sharePointUniqueId: uniqueId,
          canCreateFolder: (refreshSettings && settings) ? settings.EnableFolderCreation : currentSharepointFolder?.canCreateFolder,
          contentTypesEnabled: (refreshSettings && settings) ? settings.ContentTypesEnabled : currentSharepointFolder?.contentTypesEnabled,
          enableMinorVersions: (refreshSettings && settings) ? settings.EnableMinorVersions : currentSharepointFolder?.enableMinorVersions,
          disableOpenInApp: (refreshSettings && settings) ? settings.ExcludeFromOfflineClient === true : currentSharepointFolder?.disableOpenInApp,
          userPermissions: (refreshPermissions && permissions) ? permissions : currentSharepointFolder?.userPermissions
        });
      }
    }
  }

  const getDocumentSetSettings = async (accessToken: string, documentSetContentTypeId: string): Promise<IDocumentSetSettings> => {
    let documentSetSettings: IDocumentSetSettings;
    if (documentSetContentTypeId) {
      documentSetSettings = await SharepointContentTypeHelper.getDocumentSetSettings(accessToken, currentDirectory.webUrl, currentDirectory.sharePointListId, documentSetContentTypeId);
      setDocumentSetSettings(documentSetSettings);
    }
    return documentSetSettings;
  }

  // const getProtectionLabelsGraph = async () => {
  //   try {
  //     const protectionLabelsGraph = await getProtectionLabels();
  //     //Map protection labels to Options, and add the "None" option
  //     const protectionLabelOptions: IDropdownOption[] = [{
  //       key: null,
  //       text: "",
  //       title: ""
  //     }]
  //       .concat(
  //         protectionLabelsGraph
  //           .map((label, index) => {
  //             return {
  //               key: label.id,
  //               text: label.name,
  //               title: label.tooltip?.length > 0 ? label.tooltip : label.name,
  //             }
  //           }))
  //     //.sort((a, b) => a.key.localeCompare(b.key))
  //     setProtectionLabels(protectionLabelOptions);
  //   } catch (error) {
  //     setProtectionLabels(null);
  //   }
  // }

  const getFields = async (accessToken: string, sharedFieldIds: string[] | undefined, contentTypeId: string = null, contentTypeName: string = null) => {
    if (!isOneDriveBusiness.current) {
      const isInSPList = workingDirectories && workingDirectories.some(x => x.type === ComponentTypes.SharePointList);
      let result = await getSharePointContentTypeFields(
        accessToken,
        itemWebUrl ?? currentDirectory.webUrl,
        itemSharepointListId ?? currentDirectory.sharePointListId,
        contentTypeId,
        isInSPList ? SharepointFilters.metadata.saveSPList : SharepointFilters.metadata.save
      );

      let fields = result.map(BaseSharepointHelper.convertMetadataToFields).filter(field => field);

      if (addNameField && (!tenantDisableFileRename || isCreateFromTemplate)) {
        let nameField = {
          Choices: undefined,
          CurrentValue: null,
          DefaultValue: null,
          Id: "5cc6dc79-3710-4374-b433-61cb4a686c12",
          InternalName: sharePointInternalFileName,
          isValid: true,
          xml: {
            ColName: "nvarchar8",
            DisplayName: languageStrings.Name,
            ID: "{5cc6dc79-3710-4374-b433-61cb4a686c12}",
            Name: "Name",
            ReadOnly: disableNameField === true ? "TRUE" : "FALSE",
            Required: disableNameField === true ? "FALSE" : "TRUE",
            Sealed: "TRUE",
            ShowInEditForm: "TRUE",
            ShowInFileDlg: "FALSE",
            ShowInNewForm: "FALSE",
            SourceID: "http://schemas.microsoft.com/sharepoint/v3",
            StaticName: "Name",
            Type: "Text",
          }
        }

        if (isInfoMode && contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)) {
          const contentTypeInputName: string = `${contentTypeName} name`;
          const dataSourceHelper = new DatasourceHelper(new DatasourceService());
          const datasourceItem = dataSourceHelper.getInputDataSource(datasources, contentTypeInputName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""]);
          if (datasourceItem) {
            (nameField.xml as any).ContentTypeInputName = contentTypeInputName;
          }
        }

        fields.unshift(nameField);
      }

      if (hiddenMetadataFields?.length > 0) {
        fields = fields.filter(field => !hiddenMetadataFields.some(hiddenField => field.xml?.DisplayName?.trim().toLowerCase() === hiddenField.trim()));
      }

      if ((!isInfoMode || isCreateFromTemplate) && sharedFieldIds?.length > 0) {
        fields = fields.filter(field => !sharedFieldIds.some(sharedFieldId => field.Id === sharedFieldId));
      }
      else if (isInfoMode && sharedFieldIds?.length > 0) {
        fields = fields.map(field => {
          if (field.xml?.ReadOnly !== SharePointMetadataBooleans.true && sharedFieldIds.some(sharedFieldId => field.Id === sharedFieldId)) {
            field.xml.ReadOnly = SharePointMetadataBooleans.true;
          }
          return field;
        });
      }

      if (isBulkEdit) {
        fields = fields.filter(field => field.InternalName !== sharePointInternalTitleName);
      }

      let inputValuesToSaveFields: SharePointInputData[] = await Promise.all(fields.map(async (item, index) => {
        return await setupInputValues(item, index);
      }));
      inputValuesToSaveFields = addContentTypeMetadataMutation(inputValuesToSaveFields, contentTypeId, false);
      CacheHelper.set(DocubirdCache.spEmailPropertyMappings, emailMappings.current);

      //Typecasting here, because ".key" can be int or string
      const sensitivityLabelId: string = selectedProtectionLabel ? selectedProtectionLabel.key as string : null;
      updateSharepointMetadataToSave(inputValuesToSaveFields, sensitivityLabelId);
      setFields(fields);
      return {
        fields,
        inputValuesToSaveFields
      }
    } else if (!showStaticNameField) {
      const nameField = {
        Choices: undefined,
        CurrentValue: null,
        DefaultValue: null,
        Id: "5cc6dc79-3710-4374-b433-61cb4a686c12",
        InternalName: sharePointInternalFileName,
        isValid: true,
        xml: {
          ColName: "nvarchar8",
          DisplayName: languageStrings.Name,
          ID: "{5cc6dc79-3710-4374-b433-61cb4a686c12}",
          Name: "Name",
          Required: "TRUE",
          Sealed: "TRUE",
          ShowInEditForm: "TRUE",
          ShowInFileDlg: "FALSE",
          ShowInNewForm: "FALSE",
          SourceID: "http://schemas.microsoft.com/sharepoint/v3",
          StaticName: "Name",
          Type: "Text",
        }
      }

      // const titleField = {
      //   Choices: undefined,
      //   CurrentValue: null,
      //   DefaultValue: null,
      //   Id: "5cc6dc79-3710-4374-b433-61cb4a686c12",
      //   InternalName: sharePointInternalTitleName,
      //   isValid: true,
      //   xml: {
      //     ColName: "nvarchar8",
      //     DisplayName: languageStrings.Title,
      //     ID: "{5cc6dc79-3710-4374-b433-61cb4a686c12}",
      //     Name: sharePointInternalTitleName,
      //     Required: "FALSE",
      //     Sealed: "TRUE",
      //     ShowInEditForm: "TRUE",
      //     ShowInFileDlg: "FALSE",
      //     ShowInNewForm: "FALSE",
      //     SourceID: "http://schemas.microsoft.com/sharepoint/v3",
      //     StaticName: sharePointInternalTitleName,
      //     Type: "Text",
      //   }
      // }
      const fields = [nameField];

      const inputValuesToSaveFields: SharePointInputData[] = await Promise.all(fields.map(async (item, index) => {
        return await setupInputValues(item, index);
      }));
      CacheHelper.set(DocubirdCache.spEmailPropertyMappings, emailMappings.current);

      setFields(fields);
      updateSharepointMetadataToSave(inputValuesToSaveFields, null);

      return {
        fields
      }
    }
    return { fields: [] };
  };

  const setupInputValues = async (item, index): Promise<SharePointInputData> => {
    let defaultValue: any;

    let exchangeMailMode: boolean;
    let hasMultipleValuesMapped: boolean;
    let emailMappedProperty: string;

    if (!isInfoMode
      && mode !== ModeEnum.office
      && !DragAndDropHelper.isDroppedModeActive
      && (!generalModule.isInConentMangerMode() || (exchangeMailMode = moveTo.moveToEnabled && (moveTo.mode === ContentManagerCopyMoveModeEnum.CopySelectedEmails || moveTo.mode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails)))
      && item?.xml?.DisplayName) {
      const colName = item.xml.DisplayName.trim().toLowerCase(); //directly from sharepoint
      const spColumnNames = emailMapper.getAllListedColumns(); //list of all saved columns we should check for mapping
      const spColumnName = spColumnNames.find(col => col.toLowerCase() === colName); //either returns undefined or a sharepoint column name

      if (spColumnName) {
        if (!exchangeMailMode) {
          const mappedValue: IMappedValue = await emailMapper.getSpToEmailMapping(spColumnName, Office?.context?.mailbox, mailSendConfig?.headers);
          if (mappedValue?.value) {
            defaultValue = DefaultValueService.setMappedDefault(item, mappedValue);
          }
        }
        else {
          const selectedItems = CacheHelper.get(DocubirdCache.selectedExchangeItems);
          if (selectedItems) {
            const mappedResult = emailMapper.getOfficeJSToEmailMapping(spColumnName, selectedItems?.length === 1 ? selectedItems[0] : null);
            if (mappedResult) {
              if (selectedItems?.length === 1) {
                defaultValue = DefaultValueService.setMappedDefault(item, mappedResult.mappedVal);
              } else {
                hasMultipleValuesMapped = true;
                emailMappedProperty = mappedResult.mailPropertyName;

                const mapping: ISharePointToEmailMapping = {
                  columnName: item.InternalName,
                  emailProperty: mappedResult.mailPropertyName
                }

                emailMappings.current.push(mapping);
              }
            }
          }
        }
      }
    }

    if (!defaultValue) {
      const folderDefaultValue = locationDefaultValues.current?.find(x => x.fieldName.toLowerCase() === item.InternalName.toLowerCase())?.value;
      defaultValue = DefaultValueService.setValue(item.xml.Type, folderDefaultValue || item.DefaultValue);
    }

    const hasError = !hasMultipleValuesMapped && (item.xml.Required === SharePointMetadataBooleans.true && !defaultValue);

    return {
      index: index,
      internalName: item.InternalName,
      value: hasMultipleValuesMapped ? languageStrings.AutoSetEmailProperty.replace("{emailProperty}", emailMappedProperty) : defaultValue,
      hasError,
      type: item.xml.Percentage === SharePointMetadataBooleans.true ? `${item.xml.Type}Percentage` : item.xml.Type,
      displayName: item.xml.DisplayName,
      xml: {
        ...item.xml,
        ReadOnly: hasMultipleValuesMapped ? "TRUE" : item.ReadOnly
      },
      choices: item.Choices
    };
  };

  const setLocationDefaultValues = (defaultValues: ILibraryDefault[]) => {
    if (Array.isArray(defaultValues) && defaultValues.length > 0) {
      const decodedLocation: string = tryDecodeUriComponent(currentDirectory.directory);
      const locationDefaults: ILibraryDefault = defaultValues.find(x => x.folder.endsWith(decodedLocation));

      let libraryDefaults: ILibraryDefaultValue[] = [];
      if (locationDefaults) {

        // Get current location default values
        if (Array.isArray(locationDefaults?.values) && locationDefaults.values.length > 0) {
          libraryDefaults = libraryDefaults.concat(locationDefaults.values);
        }

        // Get all parent structure default values
        const folderUrlParts: string[] = locationDefaults.folder?.split("/");
        if (Array.isArray(folderUrlParts) && folderUrlParts.length > 0) {
          folderUrlParts.pop();
          if (Array.isArray(folderUrlParts) && folderUrlParts.length > 0) {
            let parentFolderUrl = folderUrlParts.join("/");
            let parentDefaults: ILibraryDefault = defaultValues.find(x => x.folder === parentFolderUrl);
            while (parentDefaults) {
              if (Array.isArray(parentDefaults?.values) && parentDefaults.values.length > 0) {
                libraryDefaults = libraryDefaults.concat(parentDefaults.values);
              }
              folderUrlParts.pop();
              if (Array.isArray(folderUrlParts) && folderUrlParts.length > 0) {
                parentFolderUrl = folderUrlParts.join("/");
                parentDefaults = defaultValues.find(x => x.folder === parentFolderUrl);
              }
            }
          }
        }

        locationDefaultValues.current = libraryDefaults;
      }
    }
  }

  const addContentTypeMetadataMutation = (
    inputValuesToSaveFields: SharePointInputData[],
    contentTypeId: string,
    isDefault: boolean
  ) => {
    let result = isDefault
      ? inputValuesToSaveFields
      : [
        ...inputValuesToSaveFields,
        {
          index: inputValuesToSaveFields.length,
          internalName: internalSharepointName.contentTypeId,
          value: contentTypeId,
          hasError: false,
          type: "Content"
        }
      ];

    return result;
  };

  const handleOnChange = async (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
    setFields([]);
    setSelectedContentType(item);
    var accessToken = await authModule.getSharePointAccessToken();
    const { fields } = await getFields(accessToken, documentSetSettings?.sharedFieldIds, item.key as string, item.text);
    if (fieldsChanged) fieldsChanged(fields, item.key as string);
    handleInputChange(inputValuesToSave.length, item.key, false); // last item(index) will always be the content type (inputValuesToSave.length)
  };

  const handleInputChange = (index: number, value: any, hasError: boolean) => {
    const updatedinputValuesToSave = [...inputValuesToSave];
    updatedinputValuesToSave[index].value = value;
    updatedinputValuesToSave[index].hasError = hasError;
    const sensitivityLabelId: string = selectedProtectionLabel ? selectedProtectionLabel.key as string : null;
    updateSharepointMetadataToSave(updatedinputValuesToSave, sensitivityLabelId, index);
  };

  // const savingInBackground = <SavingInBackground isOpen={isSavingInBackground} dismissPanel={dismissPanel} />;

  // const onChangeOverWrite = React.useCallback((ev: React.FormEvent<HTMLElement>, checked: boolean): void => {
  //   if (selectedIndividualFile) {
  //     setSelectedIndividualFile({ ...selectedIndividualFile, canOverwrite: checked })
  //   } else {
  //     setFilesCanOverwrite(checked);
  //   }
  // }, [selectedIndividualFile]);

  const handleProtectionLabelChange = async (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
    setSelectedProtectionLabel(item);
    // const sensitivityLabelId: string = (item.key as string)?.length > 0 ? (item.key as string) : null;
    const sensitivityLabelId: string = item.key ? (item.key as string) : null;
    updateSharepointMetadataToSave(inputValuesToSave, sensitivityLabelId);
  };

  const handleTextFieldOnchange = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue: string
  ): void => {
    // setOfficeFileName(newValue);
    setSelectedFileName(formatNameFieldValue(newValue));
  };

  const GeneratedFields =
    fields.map((item, index, _arr) => {
      let inputValueIndex = inputValuesToSave.findIndex(x => x.internalName === item.InternalName);

      return inputValueIndex !== -1 && item.InternalName !== piiConfig.privacySentitiveColumnName && item.InternalName !== piiConfig.privacySentitiveDataColumnName ? (
        <Stack.Item key={index} >
          <InputMetaDataFactory
            //siteId={currentDirectory.siteId}
            xml={item.xml}
            choices={item.Choices}
            webUrl={itemWebUrl ?? currentDirectory.webUrl}
            sharepointService={sharepointService}
            handleInputChange={handleInputChange}
            inputData={inputValuesToSave[inputValueIndex]}
            internalName={item.InternalName}
            //documentLibrary={currentDirectory.DocumentLibrary}
            regionalSetting={regionalSettings[itemWebUrl ?? currentDirectory.webUrl]}
            sharepointListId={itemSharepointListId ?? currentDirectory.sharePointListId}
            datasourceFields={datasourceFields}
          />
        </Stack.Item>
      ) : null;
    });

  const inputFields = (
    <>
      {!isOneDriveBusiness.current && showUpdateIndividualFilesButton && fileList?.filter(x => x.isSelected === true).length > 1 &&
        <ActionButton
          iconProps={multipleMetaUpdateIcon}
          styles={editFilePropertiesButtonStyles}
          allowDisabledFocus
          onClick={() => {
            setIsCMMultipleFilesMetaDataPanelOpen(true);
          }}
          text={languageStrings.UpdateIndividualFileMetaData}
        />}
      <Stack tokens={stackTokens} style={inputFieldsStackStyles}>
        {showSelectedFileCount && <Stack.Item grow>
          <Stack verticalAlign="center" horizontalAlign="center" styles={loaderStackStyles}>
            <Stack.Item align="center">
              {<Label disabled>{files.filter(x => x.isSelected).length} {languageStrings.FilesSelectedToUpload} </Label>}
            </Stack.Item>
          </Stack>
        </Stack.Item>}
        {!isOneDriveBusiness.current && currentSharepointFolder?.contentTypesEnabled === true && <Stack.Item>
          <Dropdown
            placeholder={languageStrings.SelectOption}
            selectedKey={selectedContentType ? selectedContentType.key : undefined}
            onChange={handleOnChange}
            label={languageStrings.ContentType}
            options={contentTypes}
            className="xb-sp-input"
            responsiveMode={ResponsiveMode.small}
          />
        </Stack.Item>}
        {showStaticNameField &&
          <Stack horizontal verticalAlign="start" tokens={{ childrenGap: 15 }}>
            <StackItem grow>
              <TextField
                disabled={selectedFileList?.length > 1 || moveTo.mode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails || tenantDisableFileRename}
                label={`${languageStrings.Name}`}
                type="text"
                value={selectedFileList?.length > 1 ? languageStrings.TitleAutoSet : selectedFileName}
                onChange={handleTextFieldOnchange}
                required={selectedFileList?.length === 1}
                suffix={selectedFileList?.length === 1 ? `.${selectedFileList[0].extension}` : undefined}
                errorMessage={selectedFileList?.length === 1 ? (!selectedFileName ? languageStrings.FieldRequired : (selectedFileList[0].isDuplicate ? languageStrings.FileSameName : "")) : null}
                className="xb-sp-input"
              />
            </StackItem>
          </Stack>
        }
        {GeneratedFields}
        {showProtectionDropdown && <Stack.Item>
          <Dropdown
            placeholder={languageStrings.SelectSensitivityLabel}
            selectedKey={selectedProtectionLabel ? selectedProtectionLabel.key : undefined}
            onChange={handleProtectionLabelChange}
            label={languageStrings.Sensitivity}
            options={protectionLabels}
            className="xb-sp-input"
            responsiveMode={ResponsiveMode.small}
          />
        </Stack.Item>}
        {/* {showCheckbox &&
          <Stack.Item styles={{ root: { marginTop: 20 } }}>
            <Checkbox label={languageStrings.Overwrite} checked={selectedIndividualFile ? selectedIndividualFile.canOverwrite : canOverWrite} onChange={onChangeOverWrite} />
          </Stack.Item>} */}
      </Stack>
    </>
  );

  return isLoading ? <Loader /> : <>{inputFields}</>;
};

export default connector(SaveWithMetaData);